import AttachmentModal, { AttachmentModalConfig, AttachmentService } from "@/shared/components/modals/AttachmentModal";
import { ApiReturn, GetApiReturn } from "@/modules/api.service";
import { BuscaCEP } from "@/modules/structure/endereco/correios.service";
import { DownloadFileFromDatabaseBlob } from "@/shared/utils/arquivos";
import { EstadosCivisList } from "@/modules/people/enums/estado-civil.enum";
import { FormataTelefone } from "@/shared/utils/telefones";
import { FieldError, formControlClass, formSelectClass } from "@/shared/components/forms/FormFieldFeedback";
import { numberInputValueAs } from "@/shared/utils/formZod.utils";
import { PersonProfilesFormMethods } from "./profiles/profile.types";
import { useFormState } from "react-hook-form";
import { ReadPersonDto } from "@/api.types";
import { ReadPersonFilesDto } from "@/api.types";
import { attachPersonFile, readPersonFile, deletePersonFile, downloadPersonFile, uploadPersonFoto, deletePersonFoto, fetchPersonFoto } from "../../person.service";
import { useState, useRef, useEffect } from "react";
import { validaCPF } from "@/shared/utils/cpfs";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";

interface TabDadosPessoaisProps {
  methods: PersonProfilesFormMethods
  showAnexosModal?: boolean
  onCloseAnexosModal?: () => void
}

export function TabDadosPessoais({ methods, showAnexosModal = false, onCloseAnexosModal }: TabDadosPessoaisProps) {
  const { register, setValue, watch } = methods;
  const { errors } = useFormState<ReadPersonDto>();
  const [showFotoModal, setShowFotoModal] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fotoUrlRef = useRef<string | null>(null);
  const personId = watch('id');
  const fotoPath = watch('foto');

  const revokeAndClearFotoUrl = () => {
    if (fotoUrlRef.current) {
      URL.revokeObjectURL(fotoUrlRef.current);
      fotoUrlRef.current = null;
    }
    setFotoUrl(null);
  };

  // Carrega a foto quando personId ou fotoPath mudarem
  useEffect(() => {
    let isMounted = true;
    let urlCreatedInThisEffect: string | null = null;

    if (personId && fotoPath) {
      revokeAndClearFotoUrl();
      const loadFoto = async () => {
        try {
          const blob = await fetchPersonFoto(personId);
          const url = URL.createObjectURL(blob);
          if (!isMounted) {
            URL.revokeObjectURL(url);
            return;
          }
          urlCreatedInThisEffect = url;
          fotoUrlRef.current = url;
          setFotoUrl(url);
        } catch (error) {
          if (isMounted) {
            console.error('Erro ao carregar foto:', error);
            revokeAndClearFotoUrl();
          }
        }
      };
      loadFoto();
    } else if (personId && !fotoPath) {
      revokeAndClearFotoUrl();
    }

    return () => {
      isMounted = false;
      if (urlCreatedInThisEffect) {
        URL.revokeObjectURL(urlCreatedInThisEffect);
        if (fotoUrlRef.current === urlCreatedInThisEffect) {
          fotoUrlRef.current = null;
        }
      }
    };
  }, [personId, fotoPath]);

  const handleUpdateFilesCount = () => { }

  const service: AttachmentService<ReadPersonFilesDto> = {
    enumerate: async (entityId: string | number): Promise<ApiReturn<ReadPersonFilesDto>> => {
      const result = await readPersonFile({ personId: String(entityId) });
      return GetApiReturn<ReadPersonFilesDto>(result as any);
    },

    create: async (entityId: string | number, formData: FormData) => {
      await attachPersonFile(String(entityId), formData);
    },

    download: async (attachmentId: string | number, filename?: string) => {
      const fileData = await downloadPersonFile(String(attachmentId), filename);
      DownloadFileFromDatabaseBlob(fileData.arquivo, fileData.filename);
    },

    delete: async (attachmentId: string | number) => {
      await deletePersonFile(String(attachmentId));
    },
  };

  const buildUploadData = (filename: string, entityId: string | number) => ({
    filename,
    pessoa: { id: String(entityId) },
  });

  const config: AttachmentModalConfig = {
    deleteWarningMessage: 'Arquivo removido',
  };

  const handleFotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !personId) return;

    // Valida se é uma imagem
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Valida tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB.');
      return;
    }

    setUploadingFoto(true);
    try {
      const formData = new FormData();
      formData.append('foto', file);
      const fotoPath = await uploadPersonFoto(personId, formData);
      setValue('foto', fotoPath);
      toast.success('Foto atualizada com sucesso!');
      // Recarrega a foto sem recarregar a página inteira
      try {
        const blob = await fetchPersonFoto(personId);
        const url = URL.createObjectURL(blob);
        if (fotoUrlRef.current) URL.revokeObjectURL(fotoUrlRef.current);
        fotoUrlRef.current = url;
        setFotoUrl(url);
      } catch (error) {
        console.error('Erro ao recarregar foto após upload:', error);
      }
      // Limpa o input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error(String(error));
    } finally {
      setUploadingFoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFoto = async () => {
    if (!personId) return;
    if (!confirm('Deseja realmente remover a foto?')) return;

    try {
      await deletePersonFoto(personId);
      toast.success('Foto removida com sucesso!');

      // @ts-ignore nullable.
      setValue('foto', null);
      revokeAndClearFotoUrl();
    } catch (error) {
      toast.error(String(error));
    }
  };

  return <>
    <div className="row mb-3">

      <div className="col-md-7">

        {/* DADOS PESSOAIS */}
        <div className="card mb-4 pb-2">
          <div className="card-header">
            <span>Dados Pessoais</span>
          </div>

          <div className="card-body d-flex flex-column flex-lg-row gap-3 align-items-center align-items-lg-start">
            {personId && (
              <div className="flex-shrink-0">
                <div
                  className="rounded border border-secondary bg-light d-flex align-items-center justify-content-center overflow-hidden"
                  style={{
                    width: '120px',
                    maxWidth: '100%',
                    aspectRatio: '1',
                    cursor: 'pointer',
                  }}
                  role="button"
                  title={fotoPath ? 'Clique para alterar foto' : 'Clique para adicionar foto'}
                  onClick={() => setShowFotoModal(true)}
                >
                  {fotoUrl ? (
                    <img
                      src={fotoUrl}
                      alt="Foto da pessoa"
                      className="w-100 h-100"
                      style={{ objectFit: 'cover', pointerEvents: 'none' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-muted small text-center px-1">Sem foto</span>
                  )}
                </div>
              </div>
            )}

            <div className="flex-grow-1 w-100" style={{ minWidth: 0 }}>
              <div className="row">
                <div className="form-group col-md-12 mb-4">
                  <label htmlFor="nome">Nome Completo <span className="text-danger">*</span></label>
                  <input className={formControlClass(!!errors.nome)} id="nome" {...register("nome")} />
                  <FieldError message={errors.nome?.message} />
                </div>

                <div className="form-group col-md-4">
                  <label htmlFor="cpf">CPF <span className="text-danger">*</span></label>
                  <input
                    className={formControlClass(!!errors.cpf)}
                    id="cpf"
                    maxLength={14}
                    {...register('cpf', {
                      onChange: (e) => {
                        const value = e.target.value;
                        setValue('cpf', validaCPF(value, true), { shouldValidate: true });
                      },
                    })}
                  />
                  <FieldError message={errors.cpf?.message} />
                </div>
                <div className="form-group mb-3 col-md-4">
                  <label>Data de Nascimento</label>
                  <input type="date" className="form-control" {...register("dataNascimento")} />
                </div>
                <div className="form-group mb-3 col-md-4">
                  <label>Estado Civil</label>
                  <select
                    id="estadocivil"
                    title="Estado Civil"
                    className={formSelectClass(!!errors.estadocivil)}
                    {...register('estadocivil')}
                  >
                    {EstadosCivisList.map((item) => {
                      return (
                        <option key={item.value} value={item.value}>
                          {item.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ENDEREÇO */}
        <div className="card">
          <div className="card-header">
            <span>Endereço</span>
          </div>

          <div className="card-body row">
            <div className="form-group col-md-4">
              <label>CEP</label>
              <input className="form-control" {...register("endereco.cep")} maxLength={8}
                onChange={async (e) => {
                  const cep = e.target.value;
                  if (cep.length === 8) {
                    const r = await BuscaCEP(cep);
                    if (r) {
                      // Preenche os campos com os dados retornados pelo CEP
                      setValue("endereco.logradouro", r.logradouro);
                      setValue("endereco.bairro", r.bairro);
                      setValue("endereco.cidade", r.localidade);
                      setValue("endereco.estado", r.uf);
                    }
                  }
                }}
              />
            </div>
            <div className="form-group mb-3 col-md-8">
              <label>Logradouro</label>
              <input className="form-control" {...register("endereco.logradouro")} maxLength={50} />
            </div>
            <div className="form-group mb-3 col-md-4">
              <label>Número</label>
              <input className="form-control" {...register("endereco.numero")} maxLength={6} />
            </div>
            <div className="form-group mb-3 col-md-8">
              <label>Complemento</label>
              <input className="form-control" {...register("endereco.complemento")} maxLength={50} />
            </div>
            <div className="form-group mb-3 col-md-4">
              <label>Bairro</label>
              <input className="form-control" {...register("endereco.bairro")} maxLength={50} />
            </div>
            <div className="form-group mb-3 col-md-6">
              <label>Cidade</label>
              <input className="form-control" {...register("endereco.cidade")} maxLength={60} />
            </div>
            <div className="form-group mb-3 col-md-2">
              <label>Estado</label>
              <input className="form-control" {...register("endereco.estado")} maxLength={2} />
            </div>
          </div>
        </div>


      </div>

      <div className="col-md-5">

        {/* ATRIBUTOS */}
        <div className="card mb-3">
          <div className="card-header">
            <span>Atributos</span>
          </div>
          <div className="card-body">
            <div className="form-check mt-3 ms-3" title="Permite que seja exibido na pesquisa por responsáveis por estagiários.">
              <input type="checkbox" className="form-check-input" id="podeserresponsavel"
                {...register("podeserresponsavel")}
              />
              <label className="form-check-label" htmlFor="podeserresponsavel">Pode ser resposável por estagiário.</label>
            </div>
          </div>

        </div>

        {/* E-MAILS  */}
        <div className="card mb-3">
          <div className="card-header">
            <span>E-mails</span>
          </div>
          <div className="card-body">
            <div className="form-group mb-3">
              <label>Pessoal</label>
              <input
                className={formControlClass(!!errors.email)}
                type="email"
                maxLength={50}
                {...register("email")}
              />
              <FieldError message={errors.email?.message} />
            </div>
            <div className="form-group">
              <label>Institucional <span className="text-danger">*</span></label>
              <input
                className={formControlClass(!!errors.emailInstitucional)}
                type="email"
                maxLength={50}
                {...register("emailInstitucional")}
              />
              <FieldError message={errors.emailInstitucional?.message} />
            </div>
          </div>
        </div>

        {/* CONTATOS */}
        <div className="card">
          <div className="card-header">
            <span>Telefones</span>
          </div>
          <div className="card-body row">

            <div className="col-md-4">
              <label htmlFor="telefoneddd" className="form-label">DDD:</label>
              <input
                id="telefoneddd"
                title="DDD"
                type="number"
                className={formControlClass(!!errors.telefone?.ddd)}
                {...register('telefone.ddd', { setValueAs: numberInputValueAs })}
                min={1}
                max={99}
              />
              <FieldError message={errors.telefone?.ddd?.message} />
            </div>
            <div className="col-md-8 mb-3">
              <label htmlFor="telefone_numero" className="form-label">Telefone:</label>
              <input
                id="telefone_numero"
                title="Número Telefone"
                type="text"
                className={formControlClass(!!errors.telefone?.numero)}
                maxLength={10}
                {...register('telefone.numero', {
                  onChange: (e) => {
                    const value = e.target.value;
                    setValue('telefone.numero', FormataTelefone(value), { shouldValidate: true });
                  },
                })}
              />
              <FieldError message={errors.telefone?.numero?.message} />
            </div>

            <div className="col-md-4">
              <label htmlFor="celularddd" className="form-label">DDD:</label>
              <input
                id="celularddd"
                title="DDD"
                type="number"
                className={formControlClass(!!errors.celular?.ddd)}
                min={1}
                max={99}
                {...register('celular.ddd', { setValueAs: numberInputValueAs })}
              />
              <FieldError message={errors.celular?.ddd?.message} />
            </div>
            <div className="col-md-8">
              <label htmlFor="celular_numero" className="form-label">Celular:</label>
              <input
                id="celular_numero"
                title="Número Celular"
                type="text"
                className={formControlClass(!!errors.celular?.numero)}
                maxLength={10}
                {...register('celular.numero', {
                  onChange: (e) => {
                    const value = e.target.value;
                    setValue('celular.numero', FormataTelefone(value), { shouldValidate: true });
                  },
                })}
              />
              <FieldError message={errors.celular?.numero?.message} />
            </div>

          </div>
        </div>
      </div>
    </div>

    {showAnexosModal && watch('id') && (
      <AttachmentModal
        entityId={watch('id')}
        show={showAnexosModal}
        handleHide={() => onCloseAnexosModal?.()}
        handleUpdateFilesCount={handleUpdateFilesCount}
        service={service}
        buildUploadData={buildUploadData}
        config={config}
      />
    )}

    {/* Modal de Foto */}
    {showFotoModal && personId && (
      <Modal show={showFotoModal} onHide={() => setShowFotoModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Foto da Pessoa</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {fotoUrl ? (
            <div className="mb-4">
              <img
                src={fotoUrl}
                alt="Foto da pessoa"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '8px',
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  margin: '0 auto',
                  display: 'block'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="mb-4" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <span className="text-muted">Sem foto cadastrada</span>
            </div>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFotoChange}
              disabled={uploadingFoto}
              style={{ display: 'none' }}
              id="foto-input-modal"
            />
            <label htmlFor="foto-input-modal" className="btn btn-primary me-2" style={{ cursor: uploadingFoto ? 'not-allowed' : 'pointer' }}>
              {uploadingFoto ? 'Enviando...' : fotoUrl ? 'Alterar Foto' : 'Adicionar Foto'}
            </label>
            {fotoUrl && (
              <Button
                variant="danger"
                onClick={handleDeleteFoto}
                disabled={uploadingFoto}
              >
                Remover Foto
              </Button>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFotoModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    )}
  </>
}